import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UserPlus, KeyRound, Ban, Play, Trash2, Copy, Check, ShieldAlert, Loader2, X } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { toast } from "sonner";
import PlatformLayout from "@/components/PlatformLayout";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  PLATFORM_ROLES, ROLE_LABEL, ROLE_DESCRIPTION,
  ACCOUNT_STATUS, STATUS_LABEL,
  PLATFORM_ROLE, PERMISSION,
  hasPermission, isOwner, canManageStaffRole,
} from "@/constants/platformRbac";

// ---------- helpers ----------
const statusBadge = (st) => {
  const map = {
    ACTIVE: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    INVITED: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
    SUSPENDED: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    DISABLED: "bg-rose-500/20 text-rose-300 border-rose-500/40",
  };
  return `px-2 py-0.5 rounded-full text-[10px] font-bold border ${map[st] || "bg-white/10 text-white/60 border-white/20"}`;
};

const roleBadge = (r) =>
  r === PLATFORM_ROLE.OWNER
    ? "bg-gradient-to-r from-cyan-500/30 to-purple-500/30 text-white border-cyan-400/40"
    : "bg-white/10 text-white/80 border-white/20";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
};

// ---------- Temp Password Dialog ----------
function TempPasswordDialog({ open, onClose, email, tempPassword, mode }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${email}\n${tempPassword}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Kredensial disalin ke clipboard");
    } catch {
      toast.error("Gagal menyalin — silakan salin manual");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0D1628] text-white border-white/10" data-testid="temp-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="text-amber-400" size={18} /> Password Sementara
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {mode === "create"
              ? "Staff baru berhasil dibuat. Bagikan kredensial ini SEKARANG — password sementara hanya ditampilkan sekali."
              : "Password sementara berhasil di-generate. Password lama sudah tidak valid."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-white/60 text-[11px]">Email</Label>
            <div className="mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm font-mono" data-testid="temp-password-email">{email}</div>
          </div>
          <div>
            <Label className="text-white/60 text-[11px]">Temporary Password</Label>
            <div className="mt-1 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/40 text-sm font-mono select-all break-all" data-testid="temp-password-value">{tempPassword}</div>
          </div>
          <div className="text-[11px] text-amber-300 flex items-start gap-2 bg-amber-500/10 p-3 rounded-lg">
            <ShieldAlert size={12} className="mt-0.5 flex-shrink-0" />
            <span>
              Setelah menutup dialog ini, password sementara TIDAK dapat dilihat lagi. Anda perlu reset password kalau kredensial hilang.
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={doCopy} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="temp-password-copy-btn">
            {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
            {copied ? "Tersalin" : "Copy Kredensial"}
          </Button>
          <Button onClick={onClose} variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="temp-password-close-btn">
            Sudah dibagikan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Create Staff Dialog ----------
function CreateStaffDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(PLATFORM_ROLE.SUPPORT);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!open) { setName(""); setEmail(""); setRole(PLATFORM_ROLE.SUPPORT); } }, [open]);

  const submit = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Nama dan email wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/platform/users", { name: name.trim(), email: email.trim(), platform_role: role });
      toast.success(`Staff ${r.data.user.email} berhasil dibuat`);
      onCreated(r.data);
    } catch (e) { notifyError(e, "Gagal membuat staff"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="bg-[#0D1628] text-white border-white/10" data-testid="create-staff-dialog">
        <DialogHeader>
          <DialogTitle>Tambah Staff Platform</DialogTitle>
          <DialogDescription className="text-white/60">
            Staff akan dibuat dengan status <b>INVITED</b>. Password sementara akan ditampilkan satu kali.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nama</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama lengkap" className="bg-white/5 border-white/10 text-white" data-testid="create-staff-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="staff@company.com" className="bg-white/5 border-white/10 text-white" data-testid="create-staff-email" />
          </div>
          <div>
            <Label>Role</Label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
              data-testid="create-staff-role"
            >
              {PLATFORM_ROLES.map((r) => (
                <option key={r} value={r} className="bg-[#0D1628]">
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
            <div className="text-[11px] text-white/50 mt-1">{ROLE_DESCRIPTION[role]}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" onClick={onClose} disabled={saving} data-testid="create-staff-cancel">
            Batal
          </Button>
          <Button onClick={submit} disabled={saving} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="create-staff-submit">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <UserPlus size={14} className="mr-1" />}
            Create Staff
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Edit Staff Dialog ----------
function EditStaffDialog({ open, onClose, staff, onSaved, actorUser }) {
  const [name, setName] = useState(staff?.name || "");
  const [role, setRole] = useState(staff?.platform_role || PLATFORM_ROLE.VIEWER);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (staff) { setName(staff.name || ""); setRole(staff.platform_role); }
  }, [staff]);
  if (!staff) return null;
  const canEditRole = canManageStaffRole(actorUser, staff.platform_role);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (name.trim() !== (staff.name || "")) payload.name = name.trim();
      if (canEditRole && role !== staff.platform_role) payload.platform_role = role;
      if (Object.keys(payload).length === 0) { onClose(); return; }
      await api.put(`/platform/users/${staff.id}`, payload);
      toast.success("Staff berhasil di-update");
      onSaved();
    } catch (e) { notifyError(e, "Gagal update staff"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="bg-[#0D1628] text-white border-white/10" data-testid="edit-staff-dialog">
        <DialogHeader>
          <DialogTitle>Edit Staff</DialogTitle>
          <DialogDescription className="text-white/60">{staff.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nama</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid="edit-staff-name" />
          </div>
          <div>
            <Label>Role</Label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={!canEditRole}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm disabled:opacity-40"
              data-testid="edit-staff-role"
            >
              {PLATFORM_ROLES.map((r) => <option key={r} value={r} className="bg-[#0D1628]">{ROLE_LABEL[r]}</option>)}
            </select>
            {!canEditRole && <div className="text-[11px] text-amber-300 mt-1">Anda tidak berhak mengubah role staff ini.</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" onClick={onClose} disabled={saving} data-testid="edit-staff-cancel">Batal</Button>
          <Button onClick={submit} disabled={saving} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="edit-staff-submit">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Simpan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Main Page ----------
export default function PlatformTeam() {
  const { user } = usePlatformAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [tempPwd, setTempPwd] = useState(null);
  const [actioningId, setActioningId] = useState(null);

  const canRead = hasPermission(user, PERMISSION.USERS_READ);
  const canWrite = hasPermission(user, PERMISSION.USERS_WRITE);
  const canDelete = isOwner(user);

  const load = useCallback(async () => {
    if (!canRead) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get("/platform/users");
      setItems(r.data.items || []);
    } catch (e) { notifyError(e, "Gagal memuat daftar staff"); }
    finally { setLoading(false); }
  }, [canRead]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (id, actionFn, successMsg) => {
    setActioningId(id);
    try {
      await actionFn();
      toast.success(successMsg);
      await load();
    } catch (e) { notifyError(e, "Aksi gagal"); }
    finally { setActioningId(null); }
  };

  const resetPassword = async (staff) => {
    if (!window.confirm(`Reset password ${staff.email}? Password lama akan tidak valid.`)) return;
    try {
      const r = await api.post(`/platform/users/${staff.id}/reset-password`);
      setTempPwd({ email: staff.email, password: r.data.temporary_password, mode: "reset" });
    } catch (e) { notifyError(e, "Gagal reset password"); }
  };

  const suspend = (s) => doAction(s.id, () => api.post(`/platform/users/${s.id}/suspend`), `${s.email} disuspend`);
  const activate = (s) => doAction(s.id, () => api.post(`/platform/users/${s.id}/activate`), `${s.email} diaktifkan`);
  const remove = async (s) => {
    if (!window.confirm(`Hapus staff ${s.email}? Aksi ini tidak dapat dibatalkan.`)) return;
    doAction(s.id, () => api.delete(`/platform/users/${s.id}`), `${s.email} dihapus`);
  };

  const rows = useMemo(() => items, [items]);

  // Permission-aware: staff yang bahkan tidak punya users.read
  if (!canRead) {
    return (
      <PlatformLayout title="Platform Team">
        <Card className="bg-white/5 border-white/10 p-10 text-center text-white" data-testid="team-access-denied">
          <ShieldAlert className="mx-auto text-amber-400 mb-3" size={32} />
          <div className="text-lg font-bold">Akses Ditolak</div>
          <div className="text-white/60 text-sm mt-2">
            Role Anda ({ROLE_LABEL[user?.platform_role] || user?.platform_role}) tidak memiliki permission <code className="text-cyan-300">platform.users.read</code>.
          </div>
        </Card>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout title="Platform Team">
      <div data-testid="platform-team-page">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="text-sm text-white/60">
            {items.length} anggota • Kelola akun & role Platform Team
          </div>
          {canWrite && (
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-bold"
              data-testid="team-create-btn"
            >
              <UserPlus size={14} className="mr-1.5" /> Tambah Staff
            </Button>
          )}
        </div>

        <Card className="bg-white/5 border-white/10 overflow-hidden" data-testid="team-list-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-white/70 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Nama</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Last Login</th>
                  <th className="text-right px-4 py-3">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-white/50"><Loader2 className="animate-spin inline mr-2" size={14} /> Memuat...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-white/50">Belum ada staff platform.</td></tr>
                ) : rows.map((s) => {
                  const isSelf = s.id === user?.id;
                  const busy = actioningId === s.id;
                  const canManage = canWrite && canManageStaffRole(user, s.platform_role);
                  const canDeleteThis = canDelete && !isSelf && canManage;
                  return (
                    <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.03] transition" data-testid={`team-row-${s.id}`}>
                      <td className="px-4 py-3 font-semibold">
                        {s.name || "—"}
                        {isSelf && <span className="ml-2 text-[10px] text-cyan-300">(Anda)</span>}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-white/70">{s.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${roleBadge(s.platform_role)}`} data-testid={`team-role-${s.id}`}>
                          {ROLE_LABEL[s.platform_role] || s.platform_role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={statusBadge(s.account_status)} data-testid={`team-status-${s.id}`}>
                          {STATUS_LABEL[s.account_status] || s.account_status}
                        </span>
                        {s.must_change_password && <span className="ml-1 text-[10px] text-amber-300">must-change-pw</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-white/60">{fmtDate(s.last_login_at)}</td>
                      <td className="px-4 py-3 text-right space-x-1">
                        {canManage && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-white/70 hover:bg-white/10" onClick={() => setEditTarget(s)} disabled={busy} data-testid={`team-edit-${s.id}`}>Edit</Button>
                        )}
                        {canManage && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-cyan-300 hover:bg-cyan-500/20" onClick={() => resetPassword(s)} disabled={busy} data-testid={`team-reset-${s.id}`}>
                            <KeyRound size={12} className="mr-1" />Reset
                          </Button>
                        )}
                        {canManage && !isSelf && s.account_status === ACCOUNT_STATUS.ACTIVE && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-300 hover:bg-amber-500/20" onClick={() => suspend(s)} disabled={busy} data-testid={`team-suspend-${s.id}`}>
                            <Ban size={12} className="mr-1" />Suspend
                          </Button>
                        )}
                        {canManage && !isSelf && s.account_status !== ACCOUNT_STATUS.ACTIVE && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-300 hover:bg-emerald-500/20" onClick={() => activate(s)} disabled={busy} data-testid={`team-activate-${s.id}`}>
                            <Play size={12} className="mr-1" />Activate
                          </Button>
                        )}
                        {canDeleteThis && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-300 hover:bg-rose-500/20" onClick={() => remove(s)} disabled={busy} data-testid={`team-delete-${s.id}`}>
                            <Trash2 size={12} />
                          </Button>
                        )}
                        {!canManage && <span className="text-[10px] text-white/30" data-testid={`team-noaccess-${s.id}`}>read-only</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        <CreateStaffDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(data) => {
            setCreateOpen(false);
            setTempPwd({ email: data.user.email, password: data.temporary_password, mode: "create" });
            load();
          }}
        />
        <EditStaffDialog
          open={!!editTarget}
          onClose={() => setEditTarget(null)}
          staff={editTarget}
          onSaved={() => { setEditTarget(null); load(); }}
          actorUser={user}
        />
        {tempPwd && (
          <TempPasswordDialog
            open
            onClose={() => setTempPwd(null)}
            email={tempPwd.email}
            tempPassword={tempPwd.password}
            mode={tempPwd.mode}
          />
        )}
      </div>
    </PlatformLayout>
  );
}
